import React, {useState} from 'react';
import TextAlign from "@tiptap/extension-text-align";
import {renderToString} from "react-dom/server";
import EcapHeader from "./ecap-header.jsx";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import Typography from "@tiptap/extension-typography";
import Image from '@tiptap/extension-image'
import RichTextInlineImage from "../../../../components/rich-text/rich-text-inline-image.jsx";
import {EditorContent, useEditor, EditorContext} from "@tiptap/react";
import {PaginationPlus} from "tiptap-pagination-plus";
import RichTextToolbar from "../../../../components/rich-text/rich-text-toolbar.jsx";
import "../css/ecap.css"
import {Menu, MenuButton, MenuItem, MenuItems, Transition} from "@headlessui/react";
import {FaChevronDown, FaTrash, FaCar} from "react-icons/fa";
import {FaClockRotateLeft, FaRegFilePdf, FaFloppyDisk} from "react-icons/fa6";
import RichImageEditorButton from "../../../../components/rich-text/icons/rich-image-editor-button.jsx";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import InputForm from "../../../../components/input/input-form.jsx";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const EcapWysiwyg = ({id, content, editorRef, apply, updateAndGenPdf, deleteEcap, updateHeader, headerTemplate, headerTemplateSelected, data, ecapName, setHistoryModal, showHistory, setIsLoading, enableSaveActionFeedback = false, enableDeleteActionFeedback = false}) => {
    const [headings, setHeadings] = useState([]);

    const [open, setOpen] = useState(false);
    const [passageReference, setPassageReference] = useState("");
    const feedbackLoadingClassName = "!text-gray-900 !text-white !opacity-100";
    const feedbackSuccessClassName = "!bg-green-700 !text-white !opacity-100";
    const saveFeedbackProps = enableSaveActionFeedback ? {
        actionFeedbackType: "save",
        loadingClassName: feedbackLoadingClassName,
        successClassName: feedbackSuccessClassName,
    } : {};
    const deleteFeedbackProps = enableDeleteActionFeedback ? {
        actionFeedbackType: "delete",
        loadingClassName: feedbackLoadingClassName,
    } : {};

    const handleHeadings = ({editor}) => {
        const nodes = [];
        if (!editor) return;

        editor.state.doc.descendants((node, pos) => {
            if (node.type.name === "heading" && node.attrs.level === 1 && !!node.textContent) {
                nodes.push({
                    level: node.attrs.level,
                    text: node.textContent,
                    pos,
                });
            }
        });

        setHeadings(nodes);
    }

    editorRef.current = useEditor({
        autofocus: true,
        immediatelyRender: false,
        editorProps: {
            attributes: {
                autocomplete: "off",
                autocorrect: "off",
                autocapitalize: "off",
                class: ""
            },
            handlePaste: (view, event) => {
                const items = Array.from(event.clipboardData?.items || []);
                const imageItem = items.find(item => item.type.startsWith("image/"));

                if (!imageItem) return false;

                const file = imageItem.getAsFile();
                if (!file) return false;

                const reader = new FileReader();
                reader.onload = () => {
                    editorRef.current?.chain().focus().setImage({src: reader.result}).run();
                };
                reader.readAsDataURL(file);

                return true;
            },
        },
        content,
        extensions: [
            StarterKit,
            Image,
            TextAlign.configure({types: ["heading", "paragraph", "image"]}),
            Underline,
            Typography,
            Selection,
            RichTextInlineImage,
            PaginationPlus.configure({
                pageHeight: 972.4,
                pageGap: 8,
                pageGapBorderSize: 1,
                pageBreakBackground: "#f3f3f4",
                pageHeaderHeight: 100,
                pageFooterHeight: 60,
                footerLeft: `<img src="${getAssetPath("img/sentry-logo-blue.svg")}" style="height: 8.5mm"/>`,
                footerRight: "{page}",
                headerRight: "",
                headerLeft: renderToString(<EcapHeader header={headerTemplateSelected} ecapName={ecapName}/>),
                marginTop: 10,
                marginBottom: 0,
                marginLeft: 0,
                marginRight: 0,
                contentMarginTop: 10,
                contentMarginBottom: 0,
            })
        ],
        onCreate: handleHeadings,
        onUpdate: handleHeadings
    }, [headerTemplateSelected, content]);

    const goToHeading = (pos) => {
        if (!editorRef.current) return;

        editorRef.current.chain().focus().setTextSelection(pos).scrollIntoView({inline: "end", block: "end"}).run();
    }

    if (!editorRef.current) {
        return null;
    }

    const editorClassName = [
        "editor-content",
        "[&_.ProseMirror_ul]:list-disc",
        "[&_.ProseMirror_ol]:list-decimal",
    ].join(" ");

    const addPassage = async () => {
        if (!passageReference) {
            projectUtils.swalErrorMessage("", "O campo \"Referência da passagem\" é obrigatório.", "Ok");
            return;
        }

        setIsLoading(true)

        const [procId, captureTime, plate] = passageReference.split(";");

        try {
            const response = await fetch(API_KEEPER + `/ecap/passage-image/${procId}/${captureTime}`);
            const blob = await response.blob();
            const bitmap = await createImageBitmap(blob);

            const ratio = Math.min(1, 1280 / Math.max(bitmap.width, bitmap.height));
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(bitmap.width * ratio);
            canvas.height = Math.round(bitmap.height * ratio);
            canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);

            editorRef.current?.chain().focus().setImage({ src: canvas.toDataURL("image/jpeg", 0.85) }).run();

            setOpen(false);
            setPassageReference("");
        } catch (error) {
            console.error(error);
            projectUtils.swalErrorMessage("", "Não foi possível prosseguir.", "Ok");
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <>
            <div className={"ecap flex flex-col gap-2"}>
                <EditorContext.Provider value={{editor: editorRef.current}}>
                    <RichTextToolbar
                        wrapperClass={"bg-white"}
                        imageBtn={true}
                        endActions={
                            <>
                                <button className={"rich-text-button"} onClick={() => setOpen(true)} title={"Adicionar passagem"}>
                                    <FaCar className="rich-text-button-icon"/>
                                </button>

                                <RichImageEditorButton />
                            </>
                        }
                    >
                        <div className="toolbar-group">
                            {
                                showHistory &&
                                <button className={"rich-text-button"} onClick={() => setHistoryModal(true)} title={"Histórico de versões"}>
                                    <FaClockRotateLeft className="rich-text-button-icon"/>
                                </button>
                            }
                            {
                                !data?.isFinalized &&
                                <>
                                    {
                                        updateAndGenPdf &&
                                        <Button unstyled className={"rich-text-button"} onClick={updateAndGenPdf} title={"Salvar e gerar PDF"} {...saveFeedbackProps}>
                                            <FaRegFilePdf className="rich-text-button-icon"/>
                                        </Button>
                                    }

                                    <Button unstyled className={"rich-text-button"} onClick={apply} title={"Salvar"} {...saveFeedbackProps}>
                                        <FaFloppyDisk className="rich-text-button-icon"/>
                                    </Button>

                                    {
                                        !!id &&
                                        <Button unstyled className={"rich-text-button absolute right-[15px]"} onClick={deleteEcap} title={"Excluir"} style={{right: "15px"}} {...deleteFeedbackProps}>
                                            <FaTrash className="rich-text-button-icon"/>
                                        </Button>
                                    }
                                </>
                            }
                        </div>

                        {
                            (showHistory || !data?.isFinalized) && <div className="rich-text-separator"/>
                        }

                        <div className={"toolbar-group"}>
                            <Menu>
                                <MenuButton className="rich-text-button">
                                    Cabeçalho
                                    <FaChevronDown className="rich-text-button-dropdown-small" />
                                </MenuButton>
                                <Transition
                                    enter="transition ease-out duration-75"
                                    enterFrom="opacity-0 scale-95"
                                    enterTo="opacity-100 scale-100"
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <MenuItems anchor="bottom" className="heading-dropdown">
                                        {
                                            headerTemplate?.map(t => (
                                                <MenuItem key={t.name + "-" + t.id}>
                                                    <button
                                                        className={"rich-text-button" + (headerTemplateSelected.id === t.id ? " active" : "")}
                                                        onClick={() => updateHeader(t.id)}
                                                    >
                                                        {t.name}
                                                    </button>
                                                </MenuItem>
                                            ))
                                        }
                                    </MenuItems>
                                </Transition>
                            </Menu>
                        </div>

                        <div className="rich-text-separator"/>
                    </RichTextToolbar>

                    <div className="wysiwyg-container flex gap-4" style={{maxHeight: "calc(100vh - 125px)"}}>
                        <div className="sidebar-header">
                            <div className="header-list">
                                <h3 className={"text-center"}>Sumário</h3>
                                {
                                    !headings.length &&
                                    <small className="text-center"><em>Os títulos que forem adicionados ao documento aparecerão aqui.</em></small>
                                }

                                {headings.map((item, index) => (
                                    <div
                                        key={index}
                                        className="header-item truncate"
                                        onClick={() => goToHeading(item.pos)}
                                    >
                                        {item.text}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="content-wrapper">
                            <EditorContent editor={editorRef.current} className={editorClassName}/>
                        </div>
                    </div>
                </EditorContext.Provider>
            </div>

            <Modal open={open} setOpen={setOpen} title={"Adicionar passagem"} className={"w-4/12"}>
                <Modal.Body>
                    <InputForm
                        id={"passage-reference"}
                        label={"Referência da passagem"}
                        value={passageReference}
                        onChange={({target: {value}}) => setPassageReference(value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={addPassage}>
                        Adicionar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EcapWysiwyg;